import { formatClockTime } from '@/shared/lib/time-format';
import { parseWallClock } from './wall-clock';

/**
 * One row of a "following blockers" dialog —
 * `"Appointment booked for {name} on {Mon D, YYYY} {h:mm}{a|p}"`.
 *
 * Shared by the two dialogs that list the booked appointments a change was
 * refused over:
 *   - Scheduling's Block Availability dialog, provider AND device
 *     (`POST /api/Scheduling/BlockSlot` / `BlockResourceSlot` — one handler,
 *     one blocker projection in `SchedulingController`);
 *   - Settings' working-hours dialog (`/api/Doctor/AddWorkingHours`,
 *     `PUT WorkingHour`, `DeleteWorkingHours` in `DoctorController`).
 *
 * Time contract: both controllers move each blocker's `start` into the
 * caller's zone with `DateTimeHelper.ConvertTimeFromUtc` and serialise it as a
 * wall clock with NO zone suffix — `"2026-11-10T11:00:00"` for an 11:00 Dubai
 * booking. It is read textually (`parseWallClock`, which ignores a `Z` if one
 * ever appears) and formatted with LOCAL getters. Never `new Date(start)` with
 * UTC getters: that is PROD-2527, where 11:00a rendered as 7:00a in Dubai.
 *
 * Flutter parity: `block_availability_widget.dart` blockers-state handler and
 * `doctor_schedule_tab.dart:129` render the same string (e.g. `Appointment
 * booked for Keerthy Krishnan on May 18, 2026 5:55p`).
 */

export interface BlockerRowInput {
  start?: string | null;
  patient?: { name?: string | null } | null;
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export function formatBlockerRow(b: BlockerRowInput): string {
  const who = b.patient?.name?.trim() || 'a patient';
  const d = parseWallClock(b.start);
  if (!d) return `Appointment booked for ${who}`;
  const m = MONTHS[d.getMonth()];
  const day = d.getDate();
  const y = d.getFullYear();
  return `Appointment booked for ${who} on ${m} ${day}, ${y} ${formatClockTime(d)}`;
}
